import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MailIcon, LockIcon, EyeIcon, EyeOffIcon, ArrowRightIcon } from 'lucide-react';
interface LoginProps {
  navigate: (screen: string) => void;
  viewMode?: 'app' | 'tablet';
}
export function Login({
  navigate,
  viewMode = 'app'
}: LoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      navigate('dashboard');
    }, 1500);
  };
  // Original mobile layout
  if (viewMode === 'app') {
    return <div className="flex-1 bg-slate-50 flex flex-col h-full relative overflow-hidden" data-id="element-2247">
        {/* Header Background */}
        <div className="absolute top-0 left-0 right-0 h-72 bg-[#FFD700] rounded-b-[3rem] shadow-lg shadow-[#FFD700]/20 z-0" data-id="element-2248" />
        {/* Content */}
        <div className="flex-1 flex flex-col w-full h-full absolute inset-0 z-10" data-id="element-2249">
          {/* Logo & Welcome */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="flex flex-col items-center pt-12 pb-6" data-id="element-2250">
            <img src="https://cdn.magicpatterns.com/uploads/vJQZF1GRsZggegtRVotgXV/DD_waving_hero_1.png" alt="Skydd Mascot" className="w-32 h-32 object-contain mb-4 drop-shadow-2xl" data-id="element-2251" />
            <h1 className="text-3xl font-black text-[#0D3B3B] mb-2 text-center tracking-tight" data-id="element-2252">
              Welcome Back
            </h1>
            <p className="text-[#0D3B3B]/70 text-sm text-center max-w-[240px] font-medium" data-id="element-2253">
              Log in to manage your protection plans and savings.
            </p>
          </motion.div>
          {/* Login Form */}
          <div className="flex-1 flex flex-col px-6 pb-safe overflow-y-auto" data-id="element-2254">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="bg-white rounded-[2rem] p-6 shadow-xl shadow-[#0D3B3B]/5 flex flex-col" data-id="element-2255">
              <form onSubmit={handleLogin} className="flex-1 flex flex-col" data-id="element-2256">
                <div className="space-y-5 mb-6" data-id="element-2257">
                  {/* Email Input */}
                  <div className="space-y-1.5" data-id="element-2258">
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1" data-id="element-2259">
                      Email or Phone
                    </label>
                    <div className="relative flex items-center" data-id="element-2260">
                      <div className="absolute left-4 text-gray-400" data-id="element-2261">
                        <MailIcon className="w-5 h-5" data-id="element-2262" />
                      </div>
                      <input type="text" value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter your email or phone" className="w-full bg-gray-50 border border-gray-200 rounded-2xl py-4 pl-12 pr-4 text-sm font-medium text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#2BA89D]/20 focus:border-[#2BA89D] transition-all placeholder-gray-400" required data-id="element-2263" />
                    </div>
                  </div>
                  {/* Password Input */}
                  <div className="space-y-1.5" data-id="element-2264">
                    <div className="flex justify-between items-center ml-1" data-id="element-2265">
                      <label className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-2266">
                        Password
                      </label>
                      <button type="button" onClick={() => navigate('forgot-password')} className="text-xs font-bold text-[#2BA89D] hover:text-[#0D3B3B] transition-colors" data-id="element-2267">
                        Forgot?
                      </button>
                    </div>
                    <div className="relative flex items-center" data-id="element-2268">
                      <div className="absolute left-4 text-gray-400" data-id="element-2269">
                        <LockIcon className="w-5 h-5" data-id="element-2270" />
                      </div>
                      <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" className="w-full bg-gray-50 border border-gray-200 rounded-2xl py-4 pl-12 pr-12 text-sm font-medium text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#2BA89D]/20 focus:border-[#2BA89D] transition-all placeholder-gray-400" required data-id="element-2271" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-2272">
                        {showPassword ? <EyeOffIcon className="w-5 h-5" data-id="element-2273" /> : <EyeIcon className="w-5 h-5" data-id="element-2274" />}
                      </button>
                    </div>
                  </div>
                </div>
                {/* Login Button */}
                <button type="submit" disabled={isLoading} className={`w-full py-4 rounded-2xl font-bold text-lg flex items-center justify-center gap-2 transition-all active:scale-95 mt-auto ${isLoading ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-[#0D3B3B] text-white hover:bg-[#092D2D] shadow-lg shadow-[#0D3B3B]/20'}`} data-id="element-2275">
                  {isLoading ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2276" /> : <>
                      Log In
                      <ArrowRightIcon className="w-5 h-5" data-id="element-2277" />
                    </>}
                </button>
                {/* Register Link */}
                <div className="mt-6 text-center" data-id="element-2278">
                  <p className="text-sm text-gray-500" data-id="element-2279">
                    Don't have an account?{' '}
                    <button type="button" onClick={() => navigate('register')} className="font-bold text-[#2BA89D] hover:text-[#0D3B3B] transition-colors" data-id="element-2280">
                      Sign up
                    </button>
                  </p>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </div>;
  }
  // Web/tablet split-screen layout
  return <div className="flex-1 bg-slate-50 flex flex-row h-full relative overflow-hidden" data-id="element-2281">
      {/* Left Panel - Yellow Background */}
      <div className="relative h-full w-1/2 bg-[#FFD700] flex flex-col items-center justify-center p-12" data-id="element-2282">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="flex flex-col items-center" data-id="element-2283">
          <img src="https://cdn.magicpatterns.com/uploads/vJQZF1GRsZggegtRVotgXV/DD_waving_hero_1.png" alt="Skydd Mascot" className="w-64 h-64 object-contain mb-8 drop-shadow-2xl" data-id="element-2284" />
          <h1 className="text-5xl font-black text-[#0D3B3B] mb-4 text-center tracking-tight" data-id="element-2285">
            Welcome Back
          </h1>
          <p className="text-[#0D3B3B]/70 text-lg text-center max-w-md font-medium" data-id="element-2286">
            Log in to manage your protection plans and savings.
          </p>
        </motion.div>
      </div>
      {/* Right Panel - Form */}
      <div className="flex-1 flex flex-col w-1/2 h-full justify-center p-12 bg-slate-50 overflow-y-auto" data-id="element-2287">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="bg-white rounded-[2rem] p-10 shadow-xl shadow-[#0D3B3B]/5 flex flex-col max-w-md w-full mx-auto" data-id="element-2288">
          <div className="mb-8 text-center" data-id="element-2289">
            <h2 className="text-2xl font-black text-[#0D3B3B]" data-id="element-2290">Sign In</h2>
            <p className="text-gray-500 text-sm mt-1" data-id="element-2291">
              Enter your details to continue
            </p>
          </div>
          <form onSubmit={handleLogin} className="flex-1 flex flex-col" data-id="element-2292">
            <div className="space-y-5 mb-8" data-id="element-2293">
              {/* Email Input */}
              <div className="space-y-1.5" data-id="element-2294">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1" data-id="element-2295">
                  Email or Phone
                </label>
                <div className="relative flex items-center" data-id="element-2296">
                  <div className="absolute left-4 text-gray-400" data-id="element-2297">
                    <MailIcon className="w-5 h-5" data-id="element-2298" />
                  </div>
                  <input type="text" value={email} onChange={e => setEmail(e.target.value)} placeholder="Enter your email or phone" className="w-full bg-gray-50 border border-gray-200 rounded-2xl py-4 pl-12 pr-4 text-sm font-medium text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#2BA89D]/20 focus:border-[#2BA89D] transition-all placeholder-gray-400" required data-id="element-2299" />
                </div>
              </div>
              {/* Password Input */}
              <div className="space-y-1.5" data-id="element-2300">
                <div className="flex justify-between items-center ml-1" data-id="element-2301">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-2302">
                    Password
                  </label>
                  <button type="button" onClick={() => navigate('forgot-password')} className="text-xs font-bold text-[#2BA89D] hover:text-[#0D3B3B] transition-colors" data-id="element-2303">
                    Forgot?
                  </button>
                </div>
                <div className="relative flex items-center" data-id="element-2304">
                  <div className="absolute left-4 text-gray-400" data-id="element-2305">
                    <LockIcon className="w-5 h-5" data-id="element-2306" />
                  </div>
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" className="w-full bg-gray-50 border border-gray-200 rounded-2xl py-4 pl-12 pr-12 text-sm font-medium text-[#0D3B3B] focus:outline-none focus:ring-2 focus:ring-[#2BA89D]/20 focus:border-[#2BA89D] transition-all placeholder-gray-400" required data-id="element-2307" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-2308">
                    {showPassword ? <EyeOffIcon className="w-5 h-5" data-id="element-2309" /> : <EyeIcon className="w-5 h-5" data-id="element-2310" />}
                  </button>
                </div>
              </div>
            </div>
            {/* Login Button */}
            <button type="submit" disabled={isLoading} className={`w-full py-4 rounded-2xl font-bold text-lg flex items-center justify-center gap-2 transition-all active:scale-95 ${isLoading ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-[#0D3B3B] text-white hover:bg-[#092D2D] shadow-lg shadow-[#0D3B3B]/20'}`} data-id="element-2311">
              {isLoading ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2312" /> : <>
                  Log In
                  <ArrowRightIcon className="w-5 h-5" data-id="element-2313" />
                </>}
            </button>
            {/* Register Link */}
            <div className="mt-6 text-center" data-id="element-2314">
              <p className="text-sm text-gray-500" data-id="element-2315">
                Don't have an account?{' '}
                <button type="button" onClick={() => navigate('register')} className="font-bold text-[#2BA89D] hover:text-[#0D3B3B] transition-colors" data-id="element-2316">
                  Sign up
                </button>
              </p>
            </div>
          </form>
        </motion.div>
      </div>
    </div>;
}